import { Directory } from "@capacitor/filesystem";
import { IonTabsCustomEvent } from "@ionic/core/dist/types/components";
import { IonIcon, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, setupIonicReact } from "@ionic/react";
import { LOG_LEVEL, Purchases } from "@revenuecat/purchases-capacitor";
import { addCircle, addCircleOutline, bonfire, bonfireOutline, home, homeOutline, person, personOutline, pulse, pulseOutline } from "ionicons/icons";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Redirect, Route, useHistory, useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useAppState } from "./AppListenerProvider";
import { AppContext } from "./AppStateProvider";
import { useAuth } from "./AuthProvider";
import customBackAnimation from "./components/ComponentsGestures/NavigationAnimation/customBackAnimation";
import AuthHandler from "./components/ComponentsOnboarding/AuthHandler/AuthHandler";
import Welcome from "./components/ComponentsOnboarding/Welcome/Welcome";
import WelcomeForgotPassword from "./components/ComponentsOnboarding/WelcomeForgotPassword/WelcomeForgotPassword";
import WelcomeLogin from "./components/ComponentsOnboarding/WelcomeLogin/WelcomeLogin";
import WelcomeResetPassword from "./components/ComponentsOnboarding/WelcomeResetPassword/WelcomeResetPassword";
import WelcomeSignUp from "./components/ComponentsOnboarding/WelcomeSignUp/WelcomeSignUp";
import ErrorBoundary from "./components/ComponentsUI/ErrorBoundary/ErrorBoundary";
import UploadSuccess from "./components/ComponentsUpload/UploadSuccess/UploadSuccess";
import CareersPage from "./pages/CareersPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import InvestPage from "./pages/InvestPage";
import LegalPage from "./pages/LegalPage";
import WebLandingPage from "./pages/WebLandingPage";
import useAuthService from "./hooks/useAuthService";
import { useCustomSwipeBack } from "./hooks/useCustomSwipeBack";
import { useDetermineOnboarding } from "./hooks/useDetermineOnboarding";
import { usePushNotifications } from "./hooks/usePushNotifications";
import { useTabBarVisibility } from "./hooks/useTabBarVisibility";
import { UserType } from "./models/usertype";
import BrandEventInfo from "./pages/BrandEventInfo";
import Community from "./pages/Community";
import Crew from "./pages/Crew";
import CrewUpload from "./pages/CrewUpload";
import ExplorePage from "./pages/ExplorePage";
import Filter from "./pages/Filter";
import LoadingSplash from "./pages/LoadingSplash";
import MapPage from "./pages/Map";
import Profile from "./pages/Profile";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import Settings from "./pages/Settings";
import Shop from "./pages/Shop";
import Stats from "./pages/Stats";
import TrickVideo from "./pages/TrickVideo";
import Upload from "./pages/Upload";
import AuthService from "./services/auth.service";
import PreferencesService from "./services/preferences.service";
import { fileDirectories } from "./services/utils";
import VideoCacheService from "./services/videoCache.service";

setupIonicReact({
    swipeBackEnabled: false,
});

const AppMain: React.FC = () => {
    const history = useHistory();
    const location = useLocation();

    const { state, dispatch } = useContext(AppContext);
    const { isActive, isNative, platform } = useAppState();
    const { user, isLoading, isAuthenticated, firebaseApp, auth } = useAuth();
    const { setAuthVariables } = useAuthService();
    const { checkPushNotificationPermission } = usePushNotifications();
    const { isOnboarding, isDeterminingOnboarding, getOnboardingRedirect } = useDetermineOnboarding();
    const { shouldShowTabBar, shouldDisableSwipeBack, currentTab } = useTabBarVisibility();

    const [userType, setUserType] = React.useState<string>(UserType.USER); // TODO: MAKE THIS A STATE VARIABLE
    const authService = AuthService({ user, isLoading, isAuthenticated, firebaseApp, auth }, {
        isActive,
        isNative,
        platform
    });
    const [key, setKey] = useState(0);

    // SWIPE BACK LOGIC
    const routerOutletRef = useRef<HTMLIonRouterOutletElement>(null);
    useCustomSwipeBack(routerOutletRef);

    useEffect(() => {
        document.body.classList.toggle('disable-swipe-back', shouldDisableSwipeBack);
        return () => {
            document.body.classList.remove('disable-swipe-back');
        };
    }, [shouldDisableSwipeBack]);

    // Force re-render when isOnboarding changes
    useEffect(() => {
        setKey(prevKey => prevKey + 1);
    }, [isOnboarding]);

    // Update profile route when username becomes available
    useEffect(() => {
        if (state.user?.username && location.pathname === '/profile') {
            history.replace(`/profile/${state.user.username}`);
        }
    }, [state.user?.username, location.pathname, history]);

    // PAYMENTS LOGIC
    const initializeRevenueCat = async () => {
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

        if (platform === 'ios') {
            console.log("Initializing for Apple - Revenue Cat");
            await Purchases.configure({ apiKey: 'appl_dTgMFKeUzMoMYIzYwbMzDmRnXvQ' })
        } else if (platform === 'android') {
            console.log("Initializing for Android - Revenue Cat");
            await Purchases.configure({ apiKey: 'goog_DcfrRmocYTiAaotDMpCpPBQicsW' })
        }
    }

    // AUTH LOGIC
    const setAuthVariablesHandler = async () => {
        const userType = await setAuthVariables();
        setUserType(userType);
    }

    useEffect(() => {
        if (!isLoading) {
            console.log("IS LOADING IS FALSE - Getting/Setting User Data");
            if (isAuthenticated && !isOnboarding) {
                setAuthVariablesHandler();
            }
        }

    }, [isAuthenticated, isNative, user, isLoading, isOnboarding]);

    useEffect(() => {
        const setUpSessionAndPurchases = async () => {
            console.log("Setting user state");

            const sessionId = uuidv4();
            await PreferencesService.setUserSessionId(sessionId);

            if (isNative) {
                await initializeRevenueCat();
            }
        }

        if (!isLoading) {
            console.log("IS LOADING IS FALSE - Setting Up Purchases");
            setUpSessionAndPurchases();
        }
    }, [isNative, isLoading]);

    useEffect(() => {
        if (isNative) {
            // code for clearing cache
            console.log("CLEARING CACHE")
            // code for clearing cache
            VideoCacheService.clearCache(24, fileDirectories, Directory.Cache);
        }
    }, [isNative]);

    useEffect(() => {
        const initAppFunctionality = async () => {
            if (!isActive) {
                console.log("App is in the background");
            }

            if (isActive) {
                console.log("App is in the foreground");
                if (isNative) {
                    await checkPushNotificationPermission();
                }
            }
        }

        initAppFunctionality();
    }, [isNative, isActive]);

    // AUTH ROUTING STRATEGY
    // Do not do any redirects based on conditional state - all conditional state logic should be in the component
    // If need to navigate to a different page based on conditional state, then do it in the component
    // If this navigation is to another tab, then call that tab in that component and history.go(0) if things don't work as expected

    const handleTabWillChange = (e: IonTabsCustomEvent<{ tab: string }>) => {
        console.log('Tab will change', e.detail.tab);
        if (e.detail.tab === 'home') {
            console.log('Clicked on home tab');
            history.push('/home');
        } else if (e.detail.tab === 'upload') {
            if (userType === UserType.BRAND) {
                console.log('Clicked on crew upload tab');
                history.push('/crew-upload');
            } else {
                console.log('Clicked on upload tab');
                history.push('/upload');
            }
        } else if (e.detail.tab === 'profile') {
            console.log('Clicked on profile tab');
            if (state.user?.username) {
                history.push(`/profile/${state.user.username}`);
            }
            // If username not loaded yet, let the href handle it and the effect will redirect when ready
        } else if (e.detail.tab === 'community') {
            console.log('Clicked on notifications tab');
            history.push('/community');
        } else if (e.detail.tab === 'stats') {
            console.log('Clicked on stats tab');
            history.push('/stats');
        }
    };

    if (isDeterminingOnboarding) {
        return <LoadingSplash />;
    }

    //

    // <Route path="/profile/:username" component={Profile} exact={true} />
    // <Route path="/profile" component={Profile} exact={true} />

    const renderRoutes = () => (
        <IonRouterOutlet animation={isNative ? customBackAnimation : undefined} animated={isNative} key={key} ref={routerOutletRef}>
            {/* Onboarding Routes */}
            <Route path="/flow" render={() => isOnboarding ? <Welcome /> : <Redirect to="/home" />} exact />
            <Route path="/flow/signup" render={() => isOnboarding ? <WelcomeSignUp /> : <Redirect to="/home" />} exact />
            <Route path="/flow/login" render={() => isOnboarding ? <WelcomeLogin /> : <Redirect to="/home" />} exact />
            <Route path="/auth/forgot-password" render={() => isOnboarding ? <WelcomeForgotPassword /> : <Redirect to="/home" />} exact />
            <Route path="/auth/reset-password" render={() => isOnboarding ? <WelcomeResetPassword /> : <Redirect to="/home" />} exact />
            <Route path="/admin/__/auth/handler" render={() => <AuthHandler />} exact />

            {/* Main App Routes */}
            <Route path="/home" render={() => !isOnboarding ? <ExplorePage /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/map" render={() => !isOnboarding ? <MapPage /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/stats" render={() => !isOnboarding ? <Stats /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/upload" render={() => !isOnboarding ? <Upload /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/crew-upload" render={() => !isOnboarding ? <CrewUpload /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/community" render={() => !isOnboarding ? <Community /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route
                path="/profile/:username"
                render={(props) =>
                    <Profile {...props} />
                }
                exact
            />
            <Route
                path="/profile"
                render={(props) =>
                    !isOnboarding
                        ? <Profile {...props} />
                        : <Redirect to={getOnboardingRedirect()} />
                }
                exact
            />

            {/* Shared Routes */}
            <Route path="/clip-upload/:user_id/:id" render={(props) => !isOnboarding ? <UploadSuccess {...props} /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route
                path="/clip/:user_id/:id"
                render={(props) =>
                    <TrickVideo {...props} />
                }
                exact
            />
            <Route
                path="/search/:query"
                render={(props) =>
                    !isOnboarding
                        ? <Filter {...props} />
                        : <Redirect to={getOnboardingRedirect()} />
                } exact
            />
            <Route
                path="/search"
                render={(props) =>
                    !isOnboarding ? <Filter {...props} />
                        : <Redirect to={getOnboardingRedirect()} />}
                exact
            />
            <Route path="/shop" render={() => !isOnboarding ? <Shop /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/purchase" render={() => !isOnboarding ? <PurchaseSuccessPage /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/settings" render={() => !isOnboarding ? <Settings /> : <Redirect to={getOnboardingRedirect()} />} exact />
            <Route path="/loading" render={() => !isOnboarding ? <LoadingSplash /> : <Redirect to={getOnboardingRedirect()} />} exact />

            <Route path="/crews/:username" render={(props) => <Crew {...props} />} exact />
            <Route path="/event/:id" render={(props) => <BrandEventInfo {...props} />} exact />

            {/* Redirect Routes */}
            <Route
                path="/trick/:user_id/:id"
                render={({ match }) => (
                    <Redirect to={`/clip/${match.params.user_id}/${match.params.id}`} />
                )}
                exact
            />

            {/* Web-only routes. On native, redirect to /home — passing boolean `false` as an
                IonRouterOutlet child crashes @ionic/react-router's child iteration
                (matchRoute reads `.props` on null after React converts booleans to null). */}
            <Route path="/terms" render={() => isNative ? <Redirect to="/home" /> : <LegalPage document="terms" />} exact />
            <Route path="/privacy" render={() => isNative ? <Redirect to="/home" /> : <LegalPage document="privacy" />} exact />
            <Route path="/eula" render={() => isNative ? <Redirect to="/home" /> : <LegalPage document="eula" />} exact />
            <Route path="/invest" render={() => isNative ? <Redirect to="/home" /> : <InvestPage />} exact />
            <Route path="/careers" render={() => isNative ? <Redirect to="/home" /> : <CareersPage />} exact />
            <Route path="/delete" render={() => isNative ? <Redirect to="/home" /> : <DeleteAccountPage />} exact />

            {/* Default Routes */}
            {/* On web: show the landing page for unauthenticated visitors at the root path.
                On native (iOS/Android): redirect as normal — the landing page is web-only. */}
            <Route exact path="/" render={() => {
                if (!isNative && isOnboarding) {
                    return <WebLandingPage />;
                }
                return <Redirect to={isOnboarding ? getOnboardingRedirect() : "/home"} />;
            }} />
            <Route render={() => {
                if (!isNative && isOnboarding) return <Redirect to="/" />;
                return <Redirect to={isOnboarding ? getOnboardingRedirect() : "/home"} />;
            }} />
        </IonRouterOutlet>
    );

    return (
        <ErrorBoundary>
            <IonTabs onIonTabsWillChange={handleTabWillChange}>
                {renderRoutes()}
                <IonTabBar slot="bottom" hidden={!shouldShowTabBar(location.pathname)}>
                    <IonTabButton tab="home" href={'/home'}>
                        <IonIcon icon={currentTab === '/home' ? home : homeOutline} />
                        <span>Feed</span>
                    </IonTabButton>
                    <IonTabButton tab="community" href={`/community`}>
                        <IonIcon icon={currentTab === '/community' ? bonfire : bonfireOutline} />
                        <span>Community</span>
                    </IonTabButton>
                    <IonTabButton tab="upload" href={userType !== UserType.BRAND ? '/upload' : '/crew-upload'}>
                        <IonIcon icon={currentTab === '/upload' || currentTab === '/crew-upload' ? addCircle : addCircleOutline} />
                        <span>Upload</span>
                    </IonTabButton>
                    <IonTabButton tab="stats" href={'/stats'}>
                        <IonIcon icon={currentTab === '/stats' ? pulse : pulseOutline} />
                        <span>Stats</span>
                    </IonTabButton>
                    <IonTabButton tab="profile"
                        href={state.user?.username ? `/profile/${state.user.username}` : '/profile'}>
                        <IonIcon icon={currentTab?.startsWith('/profile') ? person : personOutline} />
                        <span>You</span>
                    </IonTabButton>
                </IonTabBar>
            </IonTabs >
        </ErrorBoundary>
    );
};

{/*
Notes:

WE ONLY USE IMPORTS FROM REACT-ROUTER NOT REACT-ROUTER-DOM

Profile Route:
like instagram, if logged in, then go to your profile with username,
if not then assume it's another profile, and use that to get profile information.
Should get profile information from the username that is supplied
*/

    // Auth routes - No Tab Bar - USED AS A BACKUP -> Revert to Onboarding Flow if Log Out - Just Need to Revert Back To Login - DEPRECATED */
    // Route path="/flow/login" render={() => <AuthFlow loginInitial={true} />} exact={true} />
    //                 <Route path="/flow/login/username" render={() => <AuthFlow loginUsername={true} />} exact={true} />
    //                 <Route path="/flow/forgot-password" render={() => <AuthFlow forgotPassword={true} />} exact={true} />
    //                 <Route path="/flow/reset-password" render={() => <AuthFlow forgotPassword={true} />} exact={true} />
    //                 <Route path="/flow/signup" render={() => <AuthFlow signUpInitial={true} />} exact={true} />
    //                 <Route path="/flow/signup/email" render={() => <AuthFlow signUpEmail={true} />} exact={true} /> */
    //                 */
}

export default AppMain;